import { useState, useEffect } from "react";
import { apiClient, API } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import {
  Loader2, Plus, X, PackageCheck, FolderOpen, Globe, Check, AlertCircle,
  Image as ImageIcon,
} from "lucide-react";
import { PACKAGE_SCOPES } from "../portfolio/scopes";

const EMPTY_FORM = {
  title: "",
  client_name: "",
  location: "",
  project_type: "",
  delivered_on: "",
  short_description: "",
  portfolio_status: "Published",
};

/**
 * Close & Deliver — the blessed path for closing a delivered project, and the
 * only way a delivered project enters the portfolio. Because it writes the
 * portfolio entry, it asks for the same detail "Add a project" does rather
 * than publishing a stub for someone to complete later: title and client come
 * prefilled from the program, the rest is the PM's to fill in.
 *
 * The project's cover image travels with it — the backend copies it onto the
 * portfolio entry as its cover, so nobody uploads the same picture twice.
 */
export default function CloseAndDeliverDialog({ programId, projectName, open, onOpenChange, onClosed }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [includedScopes, setIncludedScopes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [prefill, setPrefill] = useState(null);
  const [loading, setLoading] = useState(false);

  const [dropboxUrl, setDropboxUrl] = useState("");
  const [vexosUrl, setVexosUrl] = useState("");
  const [extraLinks, setExtraLinks] = useState([]);
  const [linkTitle, setLinkTitle] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Everything the program already knows, fetched fresh each time the dialog
  // opens so a cover uploaded a minute ago is reflected here.
  useEffect(() => {
    if (!open || !programId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      apiClient.get(`/programs/${programId}/close-and-deliver/prefill`),
      apiClient.get("/portfolio/categories").catch(() => ({ data: [] })),
    ])
      .then(([pre, cats]) => {
        if (cancelled) return;
        const d = pre.data || {};
        setPrefill(d);
        setCategories(cats.data || []);
        setForm({
          title: d.title || projectName || "",
          client_name: d.client_name || "",
          location: d.location || "",
          project_type: d.project_type || "",
          delivered_on: d.delivered_on || "",
          short_description: d.short_description || "",
          portfolio_status: "Published",
        });
        setTags(d.tags || []);
        setIncludedScopes(d.included_scopes || []);
        setDropboxUrl(d.dropbox_url || "");
        setVexosUrl(d.vexos_url || "");
      })
      .catch(() => {
        if (!cancelled) setForm({ ...EMPTY_FORM, title: projectName || "" });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, programId, projectName]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const reset = () => {
    setForm(EMPTY_FORM); setTags([]); setTagInput(""); setIncludedScopes([]);
    setDropboxUrl(""); setVexosUrl(""); setExtraLinks([]); setLinkTitle(""); setLinkUrl("");
    setPrefill(null);
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) setTags([...tags, t]);
    setTagInput("");
  };

  const addLink = () => {
    const url = linkUrl.trim();
    if (!url) return;
    setExtraLinks([...extraLinks, { title: linkTitle.trim() || "Delivery link", url }]);
    setLinkTitle(""); setLinkUrl("");
  };

  const toggleScope = (key) => setIncludedScopes(prev =>
    prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
  );

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Project title is required"); return; }
    if (!dropboxUrl.trim()) { toast.error("A Dropbox link to the assets is required"); return; }
    setSubmitting(true);
    try {
      const { data } = await apiClient.post(`/programs/${programId}/close-and-deliver`, {
        dropbox_url: dropboxUrl.trim(),
        vexos_url: vexosUrl.trim() || null,
        extra_links: extraLinks,
        title: form.title.trim(),
        client_name: form.client_name.trim() || null,
        location: form.location.trim() || null,
        // Sent as a string, empty included: the PM saw the dropdown, so leaving
        // it blank means "no category" rather than "fall back to the program type".
        project_type: form.project_type,
        delivered_on: form.delivered_on.trim() || null,
        short_description: form.short_description.trim() || null,
        tags,
        included_scopes: includedScopes,
        portfolio_status: form.portfolio_status,
      });
      const emailed = data?.email?.sent ?? 0;
      const cover = data?.cover_image_synced ? " · cover carried over" : "";
      const where = data?.portfolio_status === "Draft"
        ? "Saved to the portfolio as a draft"
        : "Published to the portfolio";
      toast.success(`Project closed & delivered. ${where}${cover}${emailed ? ` · ${emailed} teammates notified` : ""}.`);
      reset();
      onOpenChange(false);
      if (onClosed) await onClosed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't close the project");
    } finally {
      setSubmitting(false);
    }
  };

  const sectionTitle = "text-xs uppercase tracking-widest text-muted-foreground";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!submitting) onOpenChange(o); }}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'var(--font-sans)' }}>Close &amp; Deliver Project</DialogTitle>
          <DialogDescription>
            Closing <strong>{projectName}</strong> publishes it to the portfolio and emails the whole team
            “New project closed and delivered.” Fill in the portfolio record below — this is the
            project's permanent entry.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading project details…
          </div>
        ) : (
          <div className="space-y-7 py-1">
            {prefill?.existing_portfolio_project && (
              <div className="flex items-start gap-2 rounded-lg border border-border bg-secondary/50 p-3 text-sm">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  This project already has a portfolio entry
                  (“{prefill.existing_portfolio_project.title}”). Closing it again creates a second one.
                </span>
              </div>
            )}

            {/* ---------- Project details ---------- */}
            <div className="space-y-4">
              <p className={sectionTitle}>Project details</p>

              <div className="space-y-2">
                <Label htmlFor="cd-title">Project Title *</Label>
                <Input id="cd-title" value={form.title} onChange={(e) => handleChange("title", e.target.value)}
                  placeholder="e.g., The Azure Residences" data-testid="close-title-input" />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cd-client">Client</Label>
                  <Input id="cd-client" value={form.client_name} onChange={(e) => handleChange("client_name", e.target.value)}
                    placeholder="e.g., Terra Group" data-testid="close-client-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cd-location">Location</Label>
                  <Input id="cd-location" value={form.location} onChange={(e) => handleChange("location", e.target.value)}
                    placeholder="e.g., Miami, Florida" data-testid="close-location-input" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cd-type">Category</Label>
                  <Select value={form.project_type} onValueChange={(v) => handleChange("project_type", v)}>
                    <SelectTrigger data-testid="close-type-select">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cd-delivered">Delivered</Label>
                  <Input id="cd-delivered" value={form.delivered_on} onChange={(e) => handleChange("delivered_on", e.target.value)}
                    placeholder="e.g., 2026 or 2026-04-18" data-testid="close-delivered-input" />
                </div>
              </div>

              <div className="space-y-2">
                <Label>What did we sell on this project?</Label>
                <p className="text-xs text-muted-foreground">
                  Each one becomes a section every department fills in. You can change this later.
                </p>
                <div className="grid grid-cols-2 gap-2 pt-1">
                  {PACKAGE_SCOPES.map(scope => {
                    const on = includedScopes.includes(scope.key);
                    return (
                      <button
                        key={scope.key}
                        type="button"
                        onClick={() => toggleScope(scope.key)}
                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border text-left transition-smooth ${
                          on ? "border-primary bg-secondary/60" : "border-border hover:bg-secondary/40"
                        }`}
                        data-testid={`close-scope-toggle-${scope.key}`}
                      >
                        <scope.Icon className={`w-4 h-4 shrink-0 ${on ? "text-foreground" : "text-muted-foreground"}`} />
                        <span className="text-sm">{scope.short}</span>
                        {on && <Check className="w-3.5 h-3.5 ml-auto text-foreground" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cd-description">Short Description</Label>
                <Textarea id="cd-description" value={form.short_description} rows={3}
                  onChange={(e) => handleChange("short_description", e.target.value)}
                  placeholder="A brief description of the project..." data-testid="close-description-input" />
              </div>

              <div className="space-y-2">
                <Label>Tags</Label>
                <div className="flex gap-2">
                  <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="Add a tag..."
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                    data-testid="close-tag-input" />
                  <Button type="button" variant="outline" onClick={addTag}>Add</Button>
                </div>
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {tags.map(tag => (
                      <span key={tag} className="inline-flex items-center gap-1 px-3 py-1 bg-secondary text-secondary-foreground rounded-full text-sm">
                        {tag}
                        <button type="button" onClick={() => setTags(tags.filter(t => t !== tag))} className="hover:text-destructive">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="cd-status">Portfolio status</Label>
                <Select value={form.portfolio_status} onValueChange={(v) => handleChange("portfolio_status", v)}>
                  <SelectTrigger data-testid="close-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Published">Published</SelectItem>
                    <SelectItem value="Draft">Draft</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Only published projects are visible to non-admins.
                </p>
              </div>
            </div>

            {/* ---------- Cover image ---------- */}
            <div className="space-y-2">
              <p className={sectionTitle}>Portfolio cover</p>
              {prefill?.has_cover_image ? (
                <div className="flex items-center gap-4 rounded-lg border border-border p-3">
                  <img
                    src={`${API}/programs/${programId}/cover-image`}
                    alt="Project cover"
                    className="w-32 h-20 object-cover rounded-md shrink-0"
                    data-testid="close-cover-preview"
                  />
                  <p className="text-sm text-muted-foreground">
                    The project's cover image becomes the portfolio cover — no need to upload it again.
                  </p>
                </div>
              ) : (
                <div className="flex items-center gap-3 rounded-lg border border-dashed border-border p-3 text-sm text-muted-foreground">
                  <ImageIcon className="w-4 h-4 shrink-0" />
                  This project has no cover image, so the portfolio entry starts without one. Add a cover
                  on the project page first and it will travel with it.
                </div>
              )}
            </div>

            {/* ---------- Delivery links ---------- */}
            <div className="space-y-4">
              <p className={sectionTitle}>Delivery links</p>

              <div>
                <Label htmlFor="cd-dropbox" className="flex items-center gap-1.5">
                  <FolderOpen className="w-4 h-4" /> Dropbox link (assets) <span className="text-destructive">*</span>
                </Label>
                <Input id="cd-dropbox" value={dropboxUrl} onChange={(e) => setDropboxUrl(e.target.value)}
                  placeholder="https://www.dropbox.com/…" className="mt-1" data-testid="close-dropbox-url" />
              </div>

              <div>
                <Label htmlFor="cd-vexos" className="flex items-center gap-1.5">
                  <Globe className="w-4 h-4" /> VEXOS platform link <span className="text-muted-foreground">(optional)</span>
                </Label>
                <Input id="cd-vexos" value={vexosUrl} onChange={(e) => setVexosUrl(e.target.value)}
                  placeholder="https://…" className="mt-1" data-testid="close-vexos-url" />
              </div>

              <div>
                <Label className="text-muted-foreground">Other delivery links (optional)</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={linkTitle} onChange={(e) => setLinkTitle(e.target.value)} placeholder="Label" className="w-1/3" />
                  <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLink(); } }}
                    placeholder="https://…" className="flex-1" />
                  <Button type="button" variant="outline" onClick={addLink} className="shrink-0">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {extraLinks.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {extraLinks.map((l, i) => (
                      <span key={i} className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-xs">
                        {l.title}
                        <button onClick={() => setExtraLinks(extraLinks.filter((_, x) => x !== i))} className="text-muted-foreground hover:text-foreground">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button className="rounded-full" onClick={submit} disabled={submitting || loading} data-testid="close-deliver-confirm">
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Closing…</> : <><PackageCheck className="w-4 h-4 mr-2" /> Close &amp; Deliver</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
